import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import apiClient from '@/api/apiClient';
import { store } from '@/store';
import { logout, sessionRestored } from '@/store/authSlice';
import {
  clearAuthenticationSession,
  refreshAuthenticationSession,
} from './authSession';

interface RetriableRequestConfig extends InternalAxiosRequestConfig {
  _authenticationRetried?: boolean;
}

const AUTH_LIFECYCLE_ENDPOINTS = [
  '/auth/login',
  '/auth/logout',
  '/auth/introspect',
  '/auth/refresh',
];

let interceptorInstalled = false;

const redirectToLogin = (): void => {
  if (window.location.pathname !== '/login') {
    window.location.assign('/login');
  }
};

export const configureAuthResponseInterceptor = (): void => {
  if (interceptorInstalled) return;
  interceptorInstalled = true;

  apiClient.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as RetriableRequestConfig | undefined;
      const isAuthenticationEndpoint = AUTH_LIFECYCLE_ENDPOINTS.some((endpoint) =>
        originalRequest?.url?.includes(endpoint));

      if (
        error.response?.status !== 401
        || !originalRequest
        || isAuthenticationEndpoint
      ) {
        return Promise.reject(error);
      }

      if (originalRequest._authenticationRetried) {
        clearAuthenticationSession();
        store.dispatch(logout());
        redirectToLogin();
        return Promise.reject(error);
      }

      originalRequest._authenticationRetried = true;

      try {
        const session = await refreshAuthenticationSession();
        store.dispatch(sessionRestored(session));
        originalRequest.headers.Authorization = `Bearer ${session.accessToken}`;
        return apiClient(originalRequest);
      } catch (refreshError: unknown) {
        clearAuthenticationSession();
        store.dispatch(logout());
        redirectToLogin();
        return Promise.reject(refreshError);
      }
    },
  );
};
